import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import RestrictedDateSelector from './RestrictedDateSelector';
import DeliverySummaryList from './DeliverySummaryList';
import SummaryStats from './SummaryStats';
import MonthlySuccessBar from './MonthlySuccessBar';

const API_URL = process.env.REACT_APP_API_URL;

function DriverResumen({ user_id, setActiveComponent }) {
  // Get today's date in YYYY-MM-DD format (local timezone)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [showCalendar, setShowCalendar] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [successStats, setSuccessStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-fetch when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchDeliverySummary();
    }
  }, [selectedDate]);

  const fetchDeliverySummary = async () => {
    if (!selectedDate) {
      setError('Por favor selecciona una fecha');
      return;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(selectedDate)) {
      setError('Formato de fecha inválido');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/api/driver/resumen`, {
        params: {
          driver_id: user_id,
          date: selectedDate
        }
      });

      setDeliveries(response.data.deliveries || []);
      // success_stats is now returned alongside deliveries from the same endpoint
      setSuccessStats(response.data.success_stats || null);

    } catch (err) {
      if (err.response) {
        setError(`Error ${err.response.status}: ${err.response.data?.error || 'Error al cargar el resumen'}`);
      } else if (err.request) {
        setError('No se pudo conectar con el servidor');
      } else {
        setError('Error al realizar la solicitud');
      }
      setDeliveries([]);
      setSuccessStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setError(null);
  };

  const handleToggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Fixed Header */}
      <header className="bg-brand-primary text-white shadow-md sticky top-0 z-50">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setActiveComponent('OptionsDriver')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Volver"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-xl font-bold font-montserrat">Resumen de Entregas</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-6">
        {/* Monthly Success Bar — always visible, auto-fetches */}
        <div className="px-4 pt-4">
          <MonthlySuccessBar user_id={user_id} />
        </div>

        {/* Date Picker Section */}
        <div className="bg-gradient-to-b from-white to-gray-50 shadow-sm border-b border-gray-200 sticky top-[60px] z-40">
          <div className="px-4 py-4">
            <RestrictedDateSelector
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              showCalendar={showCalendar}
              onToggleCalendar={handleToggleCalendar}
            />
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 font-medium">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className="px-4 py-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-brand-primary mb-3" />
              <p className="text-sm text-gray-600">Cargando resumen...</p>
            </div>
          ) : !selectedDate ? (
            <div className="text-center py-12">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ArrowLeft className="w-8 h-8 text-brand-primary transform rotate-90" />
                </div>
                <p className="text-gray-600 font-montserrat mb-2 font-medium">
                  Selecciona una fecha
                </p>
                <p className="text-sm text-gray-500">
                  Elige una fecha del calendario para ver tu resumen de entregas
                </p>
              </div>
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-12">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📦</span>
                </div>
                <p className="text-gray-600 font-montserrat mb-2 font-medium">
                  No hay entregas
                </p>
                <p className="text-sm text-gray-500">
                  No se encontraron entregas para la fecha seleccionada
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Pass successStats from the same API call */}
              <SummaryStats deliveries={deliveries} successStats={successStats} />
              <DeliverySummaryList deliveries={deliveries} />
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default DriverResumen;